import { zodResolver } from '@hookform/resolvers/zod'
import { FileText } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { FormInput, FormWrapper } from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useOptionalRequestContext } from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import { hasBillingInfo } from './utils'

const formSchema = z.object({
  org_number: z.string().optional(),
  vat_number: z.string().optional(),
  billing_email: z
    .union([z.string().email('Invalid email address'), z.literal('')])
    .optional(),
  billing_phone: z.string().optional(),
  billing_address_line1: z.string().optional(),
  billing_address_line2: z.string().optional(),
  billing_postcode: z.string().optional(),
  billing_city: z.string().optional(),
  billing_country: z.string().optional(),
  default_invoice_reference: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>
type BillingFillMode = 'quick' | 'full'

const BILLING_KEYS = [
  'org_number',
  'vat_number',
  'billing_email',
  'billing_phone',
  'billing_address_line1',
  'billing_address_line2',
  'billing_postcode',
  'billing_city',
  'billing_country',
  'default_invoice_reference',
] as const satisfies ReadonlyArray<keyof FormValues>

const emptyValues = (): FormValues => ({
  org_number: '',
  vat_number: '',
  billing_email: '',
  billing_phone: '',
  billing_address_line1: '',
  billing_address_line2: '',
  billing_postcode: '',
  billing_city: '',
  billing_country: '',
  default_invoice_reference: '',
})

/** Invoice billing fields — always inline; payment is always invoice. */
export const BillingDetails = ({
  onReadyChange,
  onBindSave,
}: {
  onReadyChange?: (ready: boolean) => void
  onBindSave?: (save: () => Promise<void>) => void
} = {}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { organization, updateOrganization } = useOrgsApi({})
  const { isDemo, quote } = useOptionalRequestContext() ?? {}
  const [fillMode, setFillMode] = useState<BillingFillMode>('quick')

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyValues(),
  })

  useEffect(() => {
    if (isDemo && quote?.organization) {
      const org = quote.organization
      methods.reset({
        org_number: org.org_number || '',
        vat_number: org.vat_number || '',
        billing_email: org.billing_email || '',
        billing_phone: '+46 70 123 45 67',
        billing_address_line1: org.billing_address_line1 || '',
        billing_address_line2: org.billing_address_line2 || '',
        billing_postcode: org.billing_postcode || '',
        billing_city: org.billing_city || '',
        billing_country: org.billing_country || '',
        default_invoice_reference: org.default_invoice_reference || '',
      })
      return
    }
    if (!organization?.data) return
    const org = organization.data
    methods.reset({
      org_number: org.org_number || '',
      vat_number: org.vat_number || '',
      billing_email: org.billing_email || '',
      billing_phone: org.billing_phone || '',
      billing_address_line1: org.billing_address_line1 || '',
      billing_address_line2: org.billing_address_line2 || '',
      billing_postcode: org.billing_postcode || '',
      billing_city: org.billing_city || '',
      billing_country: org.billing_country || '',
      default_invoice_reference: org.default_invoice_reference || '',
    })
  }, [isDemo, quote?.organization, organization?.data, methods])

  const saveField = async (values: FormValues) => {
    if (isDemo || !organization?.data?.id) return

    const current = organization.data
    const unchanged = BILLING_KEYS.every(
      (key) => (values[key] || '') === (current[key] || '')
    )
    if (unchanged) return

    try {
      await updateOrganization.mutateAsync({
        id: current.id,
        ...values,
      })
    } catch (error) {
      console.error('Failed to update billing settings:', error)
      toast({
        variant: 'destructive',
        title: t(
          'Failed to save billing details',
          'Kunde inte spara fakturauppgifter'
        ),
      })
    }
  }

  const onBlurSave = () => void saveField(methods.getValues())
  const isFull = fillMode === 'full'

  useEffect(() => {
    if (!onReadyChange) return
    const emit = (values: FormValues) => onReadyChange(hasBillingInfo(values))
    emit(methods.getValues())
    const subscription = methods.watch((values) =>
      emit(values as FormValues)
    )
    return () => subscription.unsubscribe()
  }, [methods, onReadyChange])

  useEffect(() => {
    onBindSave?.(() => saveField(methods.getValues()))
  })

  return (
    <Column className="gap-3">
      <Column className="gap-1">
        <Row className="items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h3>{t('Billing', 'Faktura')}</h3>
        </Row>
        <p className="text-sm text-muted-foreground">
          {t(
            'Orders are invoiced. Enter your organization billing details.',
            'Ordrar faktureras. Fyll i organisationens fakturauppgifter.'
          )}
        </p>
      </Column>

      <Row className="gap-1 rounded-full border border-gray-200 bg-gray-50 p-0.5 w-fit">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={cn(
            'rounded-full px-3',
            !isFull && 'bg-white shadow-sm hover:bg-white'
          )}
          onClick={() => setFillMode('quick')}
        >
          {t('Quick', 'Snabbt')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={cn(
            'rounded-full px-3',
            isFull && 'bg-white shadow-sm hover:bg-white'
          )}
          onClick={() => setFillMode('full')}
        >
          {t('Full details', 'Alla uppgifter')}
        </Button>
      </Row>

      <FormWrapper methods={methods} onSubmit={saveField}>
        <Column className="gap-3 rounded-2xl border border-gray-200 bg-white p-4">
          {isFull ? (
            <Row className="gap-3">
              <FormInput
                label={t('Organization number', 'Organisationsnummer')}
                name="org_number"
                methods={methods}
                className="flex-1"
                placeholder={t(
                  'Enter organization number',
                  'Ange organisationsnummer'
                )}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
              <FormInput
                label={t('VAT number', 'Momsregistreringsnummer')}
                name="vat_number"
                methods={methods}
                className="flex-1"
                placeholder={t(
                  'Enter VAT number',
                  'Ange momsregistreringsnummer'
                )}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
            </Row>
          ) : (
            <FormInput
              label={t('Organization number', 'Organisationsnummer')}
              name="org_number"
              methods={methods}
              className="w-full"
              placeholder={t(
                'Enter organization number',
                'Ange organisationsnummer'
              )}
              readOnly={isDemo}
              onBlur={onBlurSave}
            />
          )}

          <FormInput
            label={t('Billing email', 'E-post för faktura')}
            name="billing_email"
            methods={methods}
            type="email"
            className="w-full"
            placeholder={t('Enter billing email', 'Ange e-post för faktura')}
            readOnly={isDemo}
            onBlur={onBlurSave}
          />
          <FormInput
            label={t('Phone number', 'Telefonnummer')}
            name="billing_phone"
            methods={methods}
            type="tel"
            className="w-full"
            placeholder={t('Enter phone number', 'Ange telefonnummer')}
            readOnly={isDemo}
            onBlur={onBlurSave}
          />

          {isFull && (
            <>
              <FormInput
                label={t('Address', 'Adress')}
                name="billing_address_line1"
                methods={methods}
                className="w-full"
                placeholder={t('Street address', 'Gatuadress')}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
              <FormInput
                label={t('Address line 2', 'Adressrad 2')}
                name="billing_address_line2"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Apartment, suite, etc.',
                  'Lägenhet, avdelning, etc.'
                )}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
              <Row className="gap-3">
                <FormInput
                  label={t('Postal code', 'Postnummer')}
                  name="billing_postcode"
                  methods={methods}
                  className="w-32"
                  placeholder={t('Postcode', 'Postnummer')}
                  readOnly={isDemo}
                  onBlur={onBlurSave}
                />
                <FormInput
                  label={t('City', 'Ort')}
                  name="billing_city"
                  methods={methods}
                  className="flex-1"
                  placeholder={t('City', 'Ort')}
                  readOnly={isDemo}
                  onBlur={onBlurSave}
                />
              </Row>
              <FormInput
                label={t('Country', 'Land')}
                name="billing_country"
                methods={methods}
                className="w-full"
                placeholder={t('Country', 'Land')}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
              <FormInput
                label={t('Invoice reference', 'Fakturareferens')}
                name="default_invoice_reference"
                methods={methods}
                className="w-full"
                placeholder={t(
                  'Your reference / cost center',
                  'Er referens / kostnadsställe'
                )}
                readOnly={isDemo}
                onBlur={onBlurSave}
              />
            </>
          )}
        </Column>
      </FormWrapper>
    </Column>
  )
}
